import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config';
import { setAuthData } from '../../redux/reducers/auth.slice';
import { useAppDispatch } from '../../redux/hooks';
import { getEntitlements } from '../../redux/thunks/auth.thunk';
import UserService from '../../services/user.service';
import { Role } from '../../common/model/type/role.enum';
import { ApprovementStatus } from '../../common/model/dto/approvement-status.enum';
import { hasManagerRole, hasViewerRole } from '../../common/utils/auth.helpers';
import { setCurrentUser } from '../../redux/reducers/user.slice';

export default function useUserAuthStatus(): boolean {
  const [isInitialUserFetch, setIsInitialUserFetch] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    let isSubscribed = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // If user is not a provider or an admin or a manager, login is not allowed
          const userEntity = await UserService.getUserInfo();
          if (
            !userEntity.roles.includes(Role.Admin) &&
            !hasManagerRole(userEntity.roles) &&
            !hasViewerRole(userEntity.roles) &&
            userEntity.provider_clinic_locations?.filter(
              (providerClinicLocation) =>
                providerClinicLocation.status === ApprovementStatus.ACCEPTED
            ).length < 1
          ) {
            dispatch(setAuthData(''));
            return;
          }

          // A protected route cannot be evaluated until both session context and
          // entitlements are hydrated. Keep the app bootstrap gate closed until then.
          dispatch(setAuthData(user.uid));
          dispatch(setCurrentUser(userEntity));
          await dispatch(getEntitlements()).unwrap();
        } else {
          dispatch(setAuthData(''));
        }
      } catch (_error) {
        dispatch(setAuthData(''));
      } finally {
        if (isSubscribed) {
          setIsInitialUserFetch(false);
        }
      }
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, [dispatch]);

  return isInitialUserFetch;
}
